import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { SetupIntent } from "@stripe/stripe-js";
import { BenAdminLead, BenAdminLeadUpdatableProps } from "RegisterBenAdmin/types";
import companyToken from "Utils/companyToken";
import { prepareHeadersWithCSRFToken } from "Utils/fetchWithCSRFToken";
import { convertDataToFormDataFormat } from "Utils/formDataHelpers";

import { appendAuthParams } from "Common/TokenAuth/Utils";

interface ResponseBody {
  benAdminLead: BenAdminLead;
}

interface GetFilesArgs {
  storageKey: string;
}

interface UploadFilesArgs {
  files: File[];
  storageKey: string;
}

interface UploadedFilesResponse {
  files: string[];
}

interface PaymentSetupIntentResponse {
  clientSecret: string;
}

// Every endpoint here resolves its company through Quoting::LoadCompanyProfile, which has
// no company to fall back to once an account administers more than one, so the identifier
// has to travel with the request.
const withCompanyToken = (params: Record<string, string> = {}) => {
  const companyIDToken = companyToken();
  return new URLSearchParams({
    ...params,
    ...(companyIDToken ? { company_id_token: companyIDToken } : {}),
  }).toString();
};

export const registerBenAdminApi = createApi({
  reducerPath: "registerBenAdminApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "/api/v1/",
    prepareHeaders: prepareHeadersWithCSRFToken,
  }),

  endpoints: (builder) => ({
    getApplication: builder.query<ResponseBody, void>({
      query: () => appendAuthParams(`/ben_admin_leads?${withCompanyToken()}`),
      providesTags: ["Application"],
    }),
    updateApplication: builder.mutation<ResponseBody, BenAdminLeadUpdatableProps>({
      query: (values) => ({
        url: appendAuthParams(`/ben_admin_leads`),
        method: "PUT",
        body: {
          benAdminLead: values,
          company_id_token: companyToken(),
        },
      }),
      invalidatesTags: ["Application"],
    }),
    uploadedFiles: builder.query<UploadedFilesResponse, GetFilesArgs>({
      query: ({ storageKey }) => appendAuthParams(`/ben_admin_leads/files?${withCompanyToken({ storage_key: storageKey })}`),
      providesTags: ["Files"],
    }),
    uploadFiles: builder.mutation<{ success: true }, UploadFilesArgs>({
      query: ({ files, storageKey }) => ({
        url: appendAuthParams(`/ben_admin_leads/files?${withCompanyToken({ storage_key: storageKey })}`),
        method: "PATCH",
        body: convertDataToFormDataFormat({ files }),
      }),
      invalidatesTags: ["Files"],
    }),
    paymentSetupIntent: builder.mutation<PaymentSetupIntentResponse, void>({
      query: () => ({
        url: `/ben_admin_leads/payment_setup_intent?${withCompanyToken()}`,
        method: "POST",
      }),
    }),
    paymentSetupIntentConfirmation: builder.mutation<void, SetupIntent>({
      query: (setupIntent) => ({
        url: `/ben_admin_leads/payment_setup_intent_confirmation?${withCompanyToken()}`,
        method: "POST",
        body: { setupIntent },
      }),
      invalidatesTags: ["Application"],
    }),
  }),
  tagTypes: ["Application", "Files"],
});

export const {
  useGetApplicationQuery,
  useUpdateApplicationMutation,
  useUploadedFilesQuery,
  useUploadFilesMutation,
  usePaymentSetupIntentMutation,
  usePaymentSetupIntentConfirmationMutation,
} = registerBenAdminApi;
